/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect} from 'react';

import {Navigate, useLocation} from 'react-router';

import {AppContext} from './appContext';

import {requireMaintenanceLogin} from '../../util/maintenanceUtil';

import {AccessKey} from '../../util/interfaces';

import {getDefaultRoute, userHasRight} from '../../util/accessUtil';

import Account from '../../models/accountModel';

import User from '../../models/userModel';

import {trackPlatformPageView} from '../../util/analytics';

import {hasAdminLogin} from '../../util/adminUtil';

interface Props {
    children: React.ReactElement;
    authenticated?: boolean;
    requireAccessKey?: AccessKey | Array<AccessKey>;
    fallbackRoute?: string;
    authenticator?: (user: User | null, account: Account | null) => boolean;
    adminOnly?: boolean;
}

export default function AppRoute(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user, validatingUser} = context.userContext;
    const {fallbackRoute, children, requireAccessKey, authenticated, authenticator = () => true, adminOnly} = props;
    const location = useLocation();

    // Track page views for all authenticated users (user type filtering happens in backend)
    useEffect(() => {
        if (user?.id && !validatingUser) {
            trackPlatformPageView(location.pathname);
        }
    }, [location.pathname, user?.id, validatingUser]);

    if (validatingUser) return null;

    // Check for maintenance mode
    if (requireMaintenanceLogin()) return <Navigate to='/onderhoud' replace />;

    // Check authentication (user needs to be logged in)
    const currentPath = encodeURIComponent(location.pathname + location.search);
    if (authenticated && !user?.id.length) {
        let route = '/inloggen';
        if (!/inloggen|registreren|(wachtwoord.*)|2fa-reset/.test(currentPath)) route += `?redirect=${currentPath}`;
        return <Navigate to={route} replace />;
    }

    const defaultRoute = getDefaultRoute(account?.userRights, account?.userRole);
    // Check admin only
    if (adminOnly && !user?.isAdmin() && !hasAdminLogin())
        return <Navigate to={fallbackRoute ?? defaultRoute} replace />;

    // Check custom authenticator
    if (!authenticator(user, account)) return <Navigate to={fallbackRoute ?? defaultRoute} replace />;

    // Check access rights
    if (requireAccessKey) {
        if (!account) {
            return <Navigate to={fallbackRoute ?? defaultRoute} replace />;
        } else if (Array.isArray(requireAccessKey)) {
            if (!requireAccessKey.find((key: AccessKey) => userHasRight(account, key))) {
                return <Navigate to={fallbackRoute ?? defaultRoute} replace />;
            }
        } else if (!userHasRight(account, requireAccessKey)) {
            return <Navigate to={fallbackRoute ?? defaultRoute} replace />;
        }
    }

    // Route accepted
    return children as React.ReactElement;
}
